<template>
  <div v-if="show_service_modal"
    class="fixed inset-0 bg-gray-900 bg-opacity-75 flex justify-center items-center z-50 backdrop-blur-sm">
    <div class="bg-white rounded-md shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
      <!-- Modal Header -->
      <div class="flex justify-between items-center px-6 py-4 border-b border-gray-200">
        <h2 class="text-xl font-semibold text-blue-900">Facility Service Management</h2>
        <button @click="emit('close_service_modal')" class="text-gray-400 hover:text-gray-600">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 18" />
          </svg>
        </button>
      </div>

      <!-- Service Form Section -->
      <div class="px-6 py-4 border-b bg-gray-50">
        <h4 class="text-lg font-medium text-gray-900 mb-2">
          {{ editingIndex !== null ? 'Edit Service' : 'Add New Service' }}
        </h4>

        <div class="grid grid-cols-12 gap-4">
          <div :class="editingIndex !== null ? 'col-span-4' : 'col-span-5'">
            <label class="block text-sm font-medium text-gray-700 mb-2">Service Name</label>
            <input v-model="currentService.name" type="text" placeholder="Enter service name"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-200" />
          </div>
          <div :class="editingIndex !== null ? 'col-span-4' : 'col-span-5'">
            <label class="block text-sm font-medium text-gray-700 mb-2">Service Type</label>
            <select v-model="currentService.type"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-200">
              <option value="">Select service type</option>
              <option v-for="type in serviceTypes" :key="type" :value="type">{{ type }}</option>
            </select>
          </div>
          <div :class="editingIndex !== null ? 'col-span-4' : 'col-span-2'">
            <!-- Empty label to match height -->
            <div class="block text-sm font-medium text-gray-700 mb-2"> </div>
            <div class="flex justify-end gap-2">
              <button @click="saveService" :disabled="!currentService.name || !currentService.type || isLoading"
                class="bg-orange-400 text-white px-4 py-2 rounded-md hover:bg-orange-300 font-medium text-sm flex items-center disabled:bg-gray-300 disabled:cursor-not-allowed transition">
                <svg v-if="isLoading" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                  </path>
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    :d="editingIndex !== null ? 'M5 13l4 4L19 7' : 'M12 6v6m0 0v6m0-6h6m-6 0H6'" />
                </svg>
                {{ isLoading ? 'Saving...' : (editingIndex !== null ? 'Update Service' : 'Add Service') }}
              </button>
              <button v-if="editingIndex !== null" @click="cancelEdit"
                class="bg-gray-400 text-white px-4 py-2 rounded-md hover:bg-gray-500 font-medium text-sm transition">
                Cancel
              </button>
            </div>
          </div>
        </div>

        <!-- Error Message -->
        <div v-if="errorMessage" class="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
          {{ errorMessage }}
        </div>

        <!-- Success Message -->
        <div v-if="successMessage" class="mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-md">
          {{ successMessage }}
        </div>
      </div>

      <!-- Service List Section -->
      <div class="px-6 py-3 flex-1 overflow-hidden flex flex-col">
        <!-- Loading State -->
        <div v-if="isFetching" class="flex justify-center items-center py-8">
          <svg class="animate-spin h-5 w-5 text-orange-400" xmlns="http://www.w3.org/2000/svg" fill="none"
            viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor"
              d="M4 12A8 12 0 018 4a8 8 0 0 1 8 8v0v-8C4 0 0 5.373 0 12h4zm2 5.291A7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3">
            </path>
          </svg>
          <span class="ml-2 text-gray-600">Loading services...</span>
        </div>

        <div v-else>
          <div class="flex justify-between items-center mb-2">
            <h4 class="text-lg font-medium text-gray-900">Service List ({{ services.length }} total)</h4>
            <div class="flex items-center gap-4">
              <!-- Refresh Button -->
              <button @click="fetchServices" class="text-orange-400 hover:text-orange-600 transition">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <!-- Search -->
              <div class="relative">
                <input v-model="searchQuery" type="text" placeholder="Search services..."
                  class="pl-10 pr-4 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-200 w-64" />
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 absolute left-3 top-2.5 text-orange-400"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
          </div>

          <!-- Services Table -->
          <div class="flex-1 overflow-auto rounded-md">
            <table class="min-w-full bg-white border border-gray-200 rounded-md shadow">
              <thead class="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th class="py-2 px-4 text-left text-gray-600 font-semibold"></th>
                  <th class="py-2 px-4 text-left text-gray-600 font-semibold">Service Name</th>
                  <th class="py-2 px-4 text-left text-gray-600 font-semibold">Service Type</th>
                  <th class="py-2 px-4 text-left text-gray-600 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200">
                <tr v-if="paginatedServices.length === 0">
                  <td colspan="4" class="px-6 py-8 text-center text-gray-500">
                    {{ searchQuery ? 'No services found matching your search.' : 'No services added yet.' }}
                  </td>
                </tr>
                <tr v-for="(service, index) in paginatedServices" :key="service.id || `${service.name}-${index}`"
                  class="hover:bg-gray-50" :class="{ 'bg-orange-50': editingIndex === getOriginalIndex(index) }">
                  <td class="px-6 py-2 whitespace-nowrap text-sm text-gray-900">
                    {{ getOriginalIndex(index) + 1 }}
                  </td>
                  <td class="px-6 py-2 whitespace-nowrap text-sm text-gray-900">{{ service.name }}</td>
                  <td class="px-6 py-2 whitespace-nowrap text-sm text-gray-900">{{ service.type }}</td>
                  <td class="px-6 py-2 whitespace-nowrap text-sm font-medium">
                    <button @click="editService(getOriginalIndex(index))"
                      class="text-orange-400 hover:text-orange-600 mr-3 transition">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 inline mr-1" fill="none"
                        viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Edit
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div v-if="filteredServices.length > itemsPerPage" class="mt-4 flex items-center justify-between">
            <div class="text-sm text-gray-700">
              Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to
              {{ Math.min(currentPage * itemsPerPage, filteredServices.length) }} of
              {{ filteredServices.length }} results
            </div>
            <div class="flex items-center space-x-2">
              <button @click="previousPage" :disabled="currentPage === 1"
                class="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition">
                Previous
              </button>
              <span v-for="page in visiblePages" :key="page">
                <button v-if="page !== '...'" @click="currentPage = page" :class="{
                  'bg-blue-800 text-white': page === currentPage,
                  'bg-gray-200 text-gray-700 hover:bg-gray-300': page !== currentPage
                }" class="px-3 py-1 text-sm rounded-md transition">
                  {{ page }}
                </button>
                <span v-else class="px-3 py-1 text-sm text-gray-500">...</span>
              </span>
              <button @click="nextPage" :disabled="currentPage === totalPages"
                class="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition">
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal Footer -->
      <div class="p-6 border-t bg-gray-50">
        <div class="flex justify-end">
          <button @click="emit('close_service_modal')"
            class="bg-gray-300 text-gray-900 px-6 py-2 rounded-md hover:bg-gray-400 font-medium transition">
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { SpecializedServicesAPI } from '@/services';

// Define service types
const serviceTypes = ref([
  'Medical',
  'Dental',
  'Laboratory',
  'Laboratory Services',
  'Radiology',
  'Diagnostic',
  'Emergency',
  'Surgical',
  'Consultation',
  'Therapy',
  'Rehabilitation',
  'Pharmacy',
]);

const props = defineProps({
  show_service_modal: {
    type: Boolean,
    required: true,
  },
  facilityId: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['close_service_modal']);

// Reactive state
const services = ref([]);
const currentService = ref({ name: '', type: '' });
const editingIndex = ref(null);
const searchQuery = ref('');
const currentPage = ref(1);
const itemsPerPage = ref(10);
const isLoading = ref(false);
const isFetching = ref(false);
const errorMessage = ref(null);
const successMessage = ref(null);

// Computed properties
const filteredServices = computed(() => {
  if (!searchQuery.value) return services.value;
  const query = searchQuery.value.toLowerCase();
  return services.value.filter(service =>
    service.name.toLowerCase().includes(query) ||
    service.type.toLowerCase().includes(query)
  );
});

const totalPages = computed(() => {
  return Math.ceil(filteredServices.value.length / itemsPerPage.value);
});

const paginatedServices = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return filteredServices.value.slice(start, end);
});

const visiblePages = computed(() => {
  const total = totalPages.value;
  const current = currentPage.value;
  const pages = [];

  if (total <= 7) {
    for (let i = 1; i <= total; i++) {
      pages.push(i);
    }
  } else {
    if (current <= 4) {
      for (let i = 1; i <= 5; i++) pages.push(i);
      pages.push('...');
      pages.push(total);
    } else if (current >= total - 3) {
      pages.push(1);
      pages.push('...');
      for (let i = total - 4; i <= total; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push('...');
      for (let i = current - 1; i <= current + 1; i++) pages.push(i);
      pages.push('...');
      pages.push(total);
    }
  }

  return pages.filter(page => page !== '...' || pages.indexOf(page) === pages.lastIndexOf(page));
});

// Watchers
watch(searchQuery, () => {
  currentPage.value = 1;
});

watch(() => props.show_service_modal, (newVal) => {
  if (newVal) {
    fetchServices();
    clearMessages();
  }
});

// Methods
const fetchServices = async () => {
  try {
    isFetching.value = true;
    errorMessage.value = null;

    const response = await SpecializedServicesAPI.getAll();
    console.log('Services Response:', response);

    if (response.data && response.data.data) {
      services.value = response.data.data.map(service => ({
        id: service.id,
        name: service.name,
        type: service.service_type,
      }));
      console.log('Fetched Services:', services.value);
    } else {
      services.value = [];
    }
  } catch (error) {
    console.error('Error fetching services:', error);
    errorMessage.value = 'Failed to load services. Please try again.';
    services.value = [];
  } finally {
    isFetching.value = false;
  }
};

const saveService = async () => {
  if (!currentService.value.name.trim() || !currentService.value.type.trim()) return;

  isLoading.value = true;
  errorMessage.value = null;
  successMessage.value = null;

  try {
    if (editingIndex.value !== null) {
      // Update existing service
      const serviceData = {
        facility_id: props.facilityId,
        name: currentService.value.name,
        service_type: currentService.value.type,
      };

      const response = await SpecializedServicesAPI.update(
        services.value[editingIndex.value].id,
        serviceData
      );
      console.log('Update Response:', response);

      if (response.data && response.data.success) {
        // Update the local services array with the response data
        services.value[editingIndex.value] = {
          ...services.value[editingIndex.value],
          name: currentService.value.name,
          type: currentService.value.type,
        };
        successMessage.value = 'Service updated successfully!';
        editingIndex.value = null;
      } else {
        throw new Error(response.data?.message || 'Failed to update service');
      }
    } else {
      // Create new service
      const serviceData = {
        facility_id: props.facilityId,
        name: currentService.value.name,
        service_type: currentService.value.type,
      };

      const response = await SpecializedServicesAPI.create(serviceData);
      console.log('Create Response:', response);

      if (response.data && response.data.success) {
        const newService = {
          id: response.data.data.id,
          name: currentService.value.name,
          type: currentService.value.type,
        };
        services.value.push(newService);
        successMessage.value = 'Service added successfully!';
      } else {
        throw new Error(response.data?.message || 'Failed to create service');
      }
    }

    resetCurrentService();

    setTimeout(() => {
      successMessage.value = null;
    }, 3000);
  } catch (error) {
    console.error('Error saving service:', error);
    errorMessage.value = error.response?.data?.message || error.message || 'Failed to save service. Please try again.';
  } finally {
    isLoading.value = false;
  }
};

const editService = (index) => {
  currentService.value = {
    name: services.value[index].name,
    type: services.value[index].type,
  };
  editingIndex.value = index;
  clearMessages();
};

const cancelEdit = () => {
  editingIndex.value = null;
  resetCurrentService();
  clearMessages();
};

const resetCurrentService = () => {
  currentService.value = { name: '', type: '' };
};

const clearMessages = () => {
  errorMessage.value = null;
  successMessage.value = null;
};

const getOriginalIndex = (paginatedIndex) => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const serviceInFiltered = filteredServices.value[start + paginatedIndex];
  return services.value.findIndex(service =>
    service.id === serviceInFiltered.id ||
    (service.name === serviceInFiltered.name && service.type === serviceInFiltered.type)
  );
};

const previousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};
</script>